// 依赖
import { ref, computed } from 'vue'
// store
import { useUserStore } from '@/store/modules/user'
import { setDefaultClinicCode } from '@/store/modules/login'
// api
import { getAllClinicList } from '@/api/user'
// 多语言
import { isZhCN } from '@/locales/index'
import { t2s } from '@/common/src/utils/translate'

// 全部列表（首位的「全部」tcma-self-all 为前端注入，查询时转为空）
const duoZhenSuoJiaQuanBu = ref<Record<string, any>[]>([])
// 全部列表去除「全部」
const duoZhenSuoWuQuanBu = computed(() => {
  const availableList = duoZhenSuoJiaQuanBu.value.filter((v: Record<string, any>) => v?.clinicCode !== 'tcma-self-all')
  return availableList
})
// 仅本诊所（医师端药品模块用：去除「全部」后只留当前登录诊所）
const benZhenSuoWuQuanBu = computed(() => {
  const currentClinicCode = useUserStore().currentClinicCode
  return duoZhenSuoWuQuanBu.value.filter((v: Record<string, any>) => v?.clinicCode === currentClinicCode)
})
// 诊所name
const clinicNameMap = computed(() => {
  return duoZhenSuoJiaQuanBu.value.reduce((prev: Record<string, any>, cur: Record<string, any>) => {
    prev[cur.clinicCode] = {
      name: cur.name,
      enName: cur.enName,
      nameHandle: cur.nameHandle,
    }
    return prev
  }, {})
})
const getClinicCodeList = () => {
  return new Promise((resolve, reject) => {
    if (duoZhenSuoJiaQuanBu.value.length) {
      resolve(duoZhenSuoJiaQuanBu.value)
      return
    }
    // 诊所列表由后端按角色权限范围（/clinic/role）返回，前端不再过滤
    getAllClinicList()
      .then((res) => {
        duoZhenSuoJiaQuanBu.value = res || []
        resolve(duoZhenSuoJiaQuanBu.value)
      })
      .catch((err) => {
        reject([])
      })
  })
}
const setClinicCodeList = (param?: Record<string, any>[]) => {
  if (!param?.length) {
    duoZhenSuoJiaQuanBu.value = []
  } else {
    duoZhenSuoJiaQuanBu.value = param
  }
}
const getClinicLabelName = (clinicCode?: string, isEn?: boolean) => {
  if (!clinicCode) {
    return ''
  }
  const item = clinicNameMap.value?.[clinicCode]
  if (isEn) {
    return item?.enName || item?.nameHandle || item?.name || clinicCode || ''
  }
  // 簡體環境對缺失 handle 的繁體診所名本地轉簡兜底
  return item?.nameHandle || (isZhCN.value && item?.name ? t2s(item.name) : item?.name) || clinicCode || ''
}

export function useClinicCodeList(op?: Record<string, any>) {
  const userStore = useUserStore()
  const loadClinicCodeList = async () => {
    await getClinicCodeList()
  }
  const hasAll = op?.hasAll // 包含「全部」选项
  const onlySelf = op?.onlySelf // 仅本诊所（医师端药品模块用；组织后台不生效，直接获取全部）
  const clinicCodeList = computed(() => {
    if (onlySelf && userStore.isFromClinic) {
      return benZhenSuoWuQuanBu.value
    }
    return hasAll ? duoZhenSuoJiaQuanBu.value : duoZhenSuoWuQuanBu.value
  })
  /**
   * 搜索栏营业地点默认选中
   * 医师端：默认本诊所；本诊所不在列表 → 空着且列表不查询（canQuery=false）
   * 组织后台：默认上次选中；不曾选过或已不在列表 → 列表第一家诊所（回写记忆）
   */
  const resolveDefaultClinic = (current?: string): { clinicCode: string; canQuery: boolean } => {
    const list = clinicCodeList.value
    if (userStore.isFromClinic) {
      const selfIn = list.some((v: Record<string, any>) => v?.clinicCode === userStore.currentClinicCode)
      return { clinicCode: selfIn ? (userStore.currentClinicCode as string) : '', canQuery: selfIn }
    }
    if (current && list.some((v: Record<string, any>) => v?.clinicCode === current)) {
      return { clinicCode: current, canQuery: true }
    }
    const first = list.find((v: Record<string, any>) => v?.clinicCode && v?.clinicCode !== 'tcma-self-all')
    const clinicCode = first?.clinicCode || ''
    if (clinicCode) {
      setDefaultClinicCode(clinicCode)
    }
    return { clinicCode, canQuery: !!clinicCode }
  }
  return {
    clinicCodeList,
    duoZhenSuoWuQuanBu,
    clinicNameMap,
    getClinicLabelName,
    loadClinicCodeList,
    setClinicCodeList,
    resolveDefaultClinic,
  }
}
